/* DOCUMENTATION
  Instances:
    - Multiple Joyride instances can exist on the same page
      - If they are both set to run automatically (disableBeacon = true) they will both start at the same time and clash
      - All but one should have beacons enabled or run based off a different criteria
  
  Steps:
    - CSS selectors that return multiple results will automatically highlight the first one
    - disableBeacon determines whether the user will click to start the guide or it will start automatically
  
  Conventions:
    - Step naming preference: #joy-<page-name>-<item-description>
    - 'unassaignedSteps' should be a full list of options (past, present, future) on each page
      - Never delete steps from there unless the Joyride id is removed or the component itself has been permanently deleted
      - To use a step in an actual guide, copy and paste it from 'unassigned'
      - That way, we can delete/change individual steps within each guide and never lose or have to search for a step 

  Notes:
    - Import Jotai baseAtom for conditional logic (eg. Canvas type)
      - Also to have access to user/org info (eg. 'Welcome <user-name>)
    - Canvas:
      - Couldn't get an individual hypothesis to be highlighted
    - Look into:
      - Programatically opening menus during the joyride guide
        - Or can I expand the spotlight dimensions when the user opens a menu or something
      - Keeping track of everything that's been clicked on
        - Show quick helper guide if a menu/icon hasn't ever been clicked
      - Guides inside of features instead of pages
        - Opening chat for the first time, for example


*/

import { List, ListItem, Typography } from "@mui/material"

export const joyrideStyles = {
  options: {
    primaryColor: 'rgb(25, 118, 210)',
    zIndex: 9999,
  },
  overlay: {
    zIndex: 9998,
  },
  tooltip: {
    backgroundColor: 'rgb(25, 118, 210)',
    color: '#fafafa',
  },
  tooltipContent: {
    padding: '10px',
  },
}

const textStyles = {
  title: {
    fontSize: '18px',
    padding: '8px',
    // textAlign: 'left',
    fontWeight: 'bold',
  },
  content: {
    fontSize: '16px',
    paddingBottom: '10px',
    // textAlign: 'left',
  },
}

export const canvasUserGuides = {
  unassignedSteps: [
    {
      target: 'body',
      content: 'Welcome window',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '#joy-canvas-grid',
      content: 'Full Canvas grid',
      placement: 'auto',
    },
    {
      target: '#joy-canvas-toolbar',
      content: 'Toolbar for the canvas',
    },
    {
      // TODO: Check this class and replace with ID, maybe also custom IDs for each segment?
      target: '.canvas-hover-category-effect',
      content: 'First Canvas segment',
    },
    {
      target: '#joy-canvas-period-selector',
      content: 'Period selector',
    },
    {
      target: '#joy-canvas-action-icons',
      content: 'Layout options (SA), filters, settings',
    },
    {
      target: '#joy-canvas-title',
      content: 'Canvas title button',
    },
    {
      target: '',
      content: '',
    },
  ],
  test: [

  ],
}

export const appUserGuides = {
  unassignedSteps: [
    {
      target: 'body',
      content: 'Welcome window',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '#joy-app-mini-logo',
      content: 'Menu for all walkthrough guides (and features to come)',
    },
    {
      target: '#joy-app-lightbulb-menu',
      content: 'The expanded lightbulb menu',
      placement: 'right',
    },
    {
      target: '#joy-app-orgtree-toggle',
      content: 'Button to open or close the org tree',
    },
    {
      target: '#joy-app-team-selector',
      content: 'Quick selector for teams in your org',
    },
    {
      target: '#joy-app-quick-info',
      content: 'Quick team stats and other info',
    },
    {
      target: '#joy-app-uploads',
      content: 'Shows uploads in progress (eg. interview audio)',
    },
    {
      target: '#joy-app-account-dropdown',
      content: 'Menu of account setting and other actions ',
    },
    {
      target: '#joy-app-notifications',
      content: 'Show notifications',
    },
    {
      target: '#joy-app-chat',
      content: 'Open/close chat',
    },
    {
      target: '#joy-app-nav-canvas',
      content: 'Navigate to Canvas',
    },
    {
      target: '#joy-app-nav-discovery',
      content: 'Navigate to Interviews',
    },
    {
      target: '#joy-app-nav-insights',
      content: 'Navigate to Insights',
    },
    {
      target: '#joy-app-nav-course-videos',
      content: 'Navigate to Videos',
    },
    {
      target: '#joy-app-resources',
      content: 'Show Resources dropdown menu',
    },
    {
      target: '#joy-app-analytics',
      content: 'Show Analytics dropdown menu',
    },
    {
      target: '',
      content: '',
    },
  ],
  test: [
    // {
    //   target: '#joy-app-mini-logo',
    //   title: 'Click on this menu',
    //   content: "There's all kinds of new good stuff here",
    //   disableBeacon: true,
    // },
    // {
    //   target: '#joy-app-lightbulb-menu',
    //   content: 'The expanded lightbulb menu',
    //   placement: 'right',
    // },
  ],
  v3ExistingUserGuide: [
    {
      target: 'body',
      title: (<Typography sx={{...textStyles.title, fontSize: '20px', padding: '20px'}}>Welcome to Innovation Within’s Discovery Platform!</Typography>),
      content: (<>
          <Typography sx={{...textStyles.content, fontSize: '18px'}}>We’re so excited you’re here, and we can’t wait to see what you create!</Typography>
          <Typography sx={{...textStyles.content, fontSize: '18px'}}>Behind this quick walk-through is our latest upgrade, including a speedier, more polished UI! It’s the same structure but cleaned up for a more intuitive flow. We’ve added a handful of NEW features, too.</Typography>
          <Typography sx={{...textStyles.content, fontSize: '18px'}}>Let’s show you around!</Typography>
          <button>Click here to go to the last step of the guide</button>
        </>),
      placement: 'center',
      disableBeacon: true,
      styles: { tooltip: {width: '650px'}, tooltipContent: {width: '100%', } },
      subGuides: [1,2,3,4]
    },
    {
      target: '#joy-app-mini-logo',
      title: (<Typography></Typography>),
      // title: "Click on our logo to access a new menu",
      content: (<Typography sx={textStyles.content}>Click on Our Logo to Access a New Menu</Typography>),
      disableOverlayClose: true,
      hideCloseButton: true,
      hideFooter: true,
      spotlightClicks: true,
      styles: { tooltip: {width: '266px'}},
    },
    {
      target: '#joy-app-lightbulb-menu',
      title: (<Typography sx={textStyles.title}>Click on our logo for help</Typography>),
      content: (<List sx={{ padding: '0px 0px 0px 0px' }}>
        <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography sx={textStyles.content}>* You can find in-app guides for each page and feature in the platform</Typography></ListItem>
        <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography sx={textStyles.content}>* The Knowledge Base contains articles full of tips for approaching customer discovery (for example, creating hypotheses and interviewing)</Typography></ListItem>
        <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography sx={textStyles.content}>* <i>Something about feature suggestions</i></Typography></ListItem>
      </List>),
      placement: 'right',
      spotlightClicks: false,
    },
    {
      target: 'body',
      title: (<Typography sx={{...textStyles.title, fontSize: '20px'}}>Our New Features Include the Following:</Typography>),
      content: (<List sx={{ padding: '0px 0px 0px 0px' }}>
        <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography sx={textStyles.content}>* Custom Canvases (set by your instructors)</Typography></ListItem>
        <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography sx={textStyles.content}>* Custom Categories/Insights</Typography></ListItem>
        <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography sx={textStyles.content}>* New Insights Page</Typography></ListItem>
        <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography sx={textStyles.content}>* Bulk Interview Uploader, Including Videos!</Typography></ListItem>
        <ListItem sx={{ padding: '0px 0px 0px 0px' }}><Typography sx={textStyles.content}>* Transcription Editor Upgrades</Typography></ListItem>
      </List>),      
      placement: 'center',
      styles: { tooltip: {width: '600px'}, tooltipContent: {width: '100%', } },
    },
    // {
    //   target: '#joy-app-nav-canvas',
    //   title: 'Canvases tailored to your needs',
    //   content: 'We now support custom canvases. Hate the BMC? Tell us how many boxes you want and what to name them',
    // },
    // {
    //   target: '#joy-app-nav-discovery',
    //   title: 'Custom Categories',
    //   content: "Same insight creation process, but now with way more labels to categorize them. If you don't like our category sets, make one of your own - - Also say something about the new transcription editor",
    // },
    // {
    //   target: '#joy-app-nav-insights',
    //   content: 'Search, sort, and filter insights based on your custom categories',
    // },
    // {
    //   target: '#joy-app-uploads',
    //   content: 'Upload multiple audio files at once in the background. See the progress of your uploads here - - Something about videos?',
    // },
  ]
}

export const discoveryUserguides = {
  unassignedSteps: [
    {
      target: '#joy-discovery-new',
      content: 'Create new interview',
    },
    {
      target: '#joy-discovery-templates',
      content: 'View templates',
    },
    {
      target: '#joy-discovery-audio-import',
      content: 'Import audio',
    },
    {
      target: '#joy-discovery-period-selector',
      content: 'Selected period to filter interview results',
    },
    {
      target: '#joy-discovery-interview-table',
      content: 'Table of interviews. Hover the column headers to sort by any column',
      placement: 'right',
    },
    {
      target: '#joy-discovery-focus-mode',
      content: "Place app in 'focus mode'. This hides everything to the left of the text editor.",
    },
    {
      // WARNING: Don't use this until the height issue is fixed. This will distort the page
      // target: '#joy-discovery-text-editor',
      // content: "This is where you can find notes and transcripts for your currently selected interview",
    },
    {
      target: '#joy-discovery-custom-categories',
      content: "Custom categories. Favorites, active tags, and search/selector for available categories",
      placement: 'left',
    },
    {
      target: '',
      content: '',
    },
  ],
  test: [
    {
      // target: '#joy-discovery-custom-categories',
      // content: "Custom categories. Favorites, active tags, and search/selector for available categories",
      // placement: 'left',
      // disableBeacon: true,
    },
  ]
}

export const insightsUserGuides = {
  unassignedSteps: [
    {
      target: '',
      content: '',
    },
    {
      target: '',
      content: '',
    },
  ],
  test: [
    {
      target: '',
      content: '',
      disableBeacon: true,
    },
  ]
}

/*

import Joyride from 'react-joyride';

<Joyride
steps={canvasUserGuides.test} 
continuous={true}
run={true}
debug={true}
disableScrolling={true}
spotlightClicks={true}
/>

*/